import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchEmspCommandFilters,
  fetchEmspCommands,
  setSortData,
  updateEMSPCommandsFilters,
} from '../../../../redux/slices/OCPI/EMSPCommandsSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiCommand {
  command_id: string;
  command?: string;
  status?: string;
  result?: string;
  result_message?: string;
  cpo_party_id?: string;
  cpo_country_code?: string;
  response_url?: string;
  resolved_at?: string;
  _objectId?: string;
  _lastUpdated?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
  data?: {
    session_id?: string;
    location?: {
      id?: string;
      evse_uid?: string;
      connector_id?: string;
    };
  };
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Renders a status badge for a command.
 * @param {any} props The props.
 * @param {any} props.status The status.
 */
function StatusBadge({ status }: Readonly<{ status: string }>) {
  let modifier = 'cpo-session-status-badge--pending';
  if (status === 'ACCEPTED') {
    modifier = 'cpo-session-status-badge--active';
  } else if (status === 'SUCCESS' || status === 'COMPLETED') {
    modifier = 'cpo-session-status-badge--completed';
  } else if (status === 'REJECTED' || status === 'FAILED' || status === 'TIMEOUT') {
    modifier = 'cpo-session-status-badge--invalid';
  }

  return <span className={`cpo-session-status-badge ${modifier}`}>{status}</span>;
}

// ── Command Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders the command drill-down modal showing raw command detail.
 * @param {any} props The props.
 * @param {any} props.command The command details.
 * @param {any} props.onClose The close callback.
 */
function CommandDrillDownModal({
  command,
  onClose,
}: Readonly<{
  command: OcpiCommand;
  onClose: () => void;
}>) {
  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-session-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{command.command_id}</h3>
              <p className="cpo-drilldown-subtitle">
                {command.status ? <StatusBadge status={command.status} /> : '–'}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <CloseIcon width={30} height={30} />
            </button>
          </div>

          {/* Raw command detail */}
          <div className="cpo-drilldown-body">
            <pre className="cpo-session-drilldown-detail">{JSON.stringify(command, null, 2)}</pre>
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

/**
 * EmspCommandsPanel component for viewing eMSP OCPI commands.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @param {any} props.filterCountryCode Optional country code of the CPO to filter by.
 * @param {any} props.filterPartyId Optional party ID of the CPO to filter by.
 * @param {any} props.refreshTrigger Optional number that triggers a reload when incremented.
 */
export default function EmspCommandsPanel({
  countryCode,
  partyId,
  filterCountryCode,
  filterPartyId,
  refreshTrigger,
}: Readonly<{
  countryCode: string;
  partyId: string;
  filterCountryCode?: string;
  filterPartyId?: string;
  refreshTrigger?: number;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    commands,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.emspCommands);
  const [selectedCommand, setSelectedCommand] = useState<OcpiCommand | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches eMSP commands for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = useCallback(
    (data: ApiRequest) => {
      if (!loading) {
        dispatch(
          fetchEmspCommands({
            countryCode,
            partyId,
            counterPartyId: filterPartyId,
            counterCountryCode: filterCountryCode,
            ...data,
          }),
        );
      }
    },
    [loading, dispatch, countryCode, partyId, filterPartyId, filterCountryCode],
  );

  useEffect(() => {
    dispatch(
      fetchEmspCommands({
        countryCode,
        partyId,
        counterPartyId: filterPartyId,
        counterCountryCode: filterCountryCode,
      }),
    );
  }, [countryCode, partyId, filterPartyId, filterCountryCode, dispatch, refreshTrigger]);

  interface CommandRow {
    id: string;
    type: string;
    status: string;
    result: string;
    resultMessage: string;
    sessionId: string;
    partyId: string;
    countryCode: string;
    resolvedAt: string;
    lastUpdated: string;
    _createdAt?: string;
    createdAt: string;
    raw: OcpiCommand;
  }

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof CommandRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {CommandRow} currentRow The current command row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: CommandRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:commandId'),
      accessorKey: 'id' as keyof CommandRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the command ID in a code badge.
       * @param {any} row The command row.
       */
      customData: (row: CommandRow) => <code className="cpo-session-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:type'),
      accessorKey: 'type' as keyof CommandRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:status'),
      accessorKey: 'status' as keyof CommandRow,
      sortAllow: false,
      /**
       * Renders the status badge.
       * @param {any} row The command row.
       */
      customData: (row: CommandRow) => <StatusBadge status={row.status} />,
    },
    {
      label: t('ocpi:result'),
      accessorKey: 'result' as keyof CommandRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:resultMessage'),
      accessorKey: 'resultMessage' as keyof CommandRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:sessionId'),
      accessorKey: 'sessionId' as keyof CommandRow,
      sortAllow: false,
      /**
       * Renders the session ID in a code badge.
       * @param {any} row The command row.
       */
      customData: (row: CommandRow) => {
        if (!row.sessionId || row.sessionId === '–') return <span>–</span>;
        return <code className="cpo-session-id-badge">{String(row.sessionId).slice(0, 18)}</code>;
      },
    },
    {
      label: t('ocpi:partyId'),
      accessorKey: 'partyId' as keyof CommandRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:countryCode'),
      accessorKey: 'countryCode' as keyof CommandRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:issuedAt'),
      accessorKey: 'resolvedAt' as keyof CommandRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof CommandRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated'),
      accessorKey: 'lastUpdated' as keyof CommandRow,
      sortAllow: false,
    },
  ];

  const tableData: CommandRow[] = commands.map((command: OcpiCommand) => {
    const commandId = command.command_id || command._objectId || '';
    const dateString = command.resolved_at || command._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const resolvedAt = command.resolved_at ? new Date(command.resolved_at).toLocaleString() : '–';
    const createdAt =
      command._createdAt ||
      command.createdAt ||
      (command as any).created_at ||
      (command as any).created ||
      '';

    const rawData: any = command.data;
    const sessionId =
      rawData?.session_id ||
      rawData?.sessionId ||
      rawData?.location?.id ||
      rawData?.location?.evse_uid ||
      (command as any)?.session_id ||
      (command as any)?.sessionId ||
      '–';

    return {
      id: commandId,
      type: command.command || '–',
      status: command.status || t('ocpi:unknown'),
      result: command.result || '–',
      resultMessage: command.result_message || '–',
      sessionId,
      partyId: command.cpo_party_id || '–',
      countryCode: command.cpo_country_code || '–',
      resolvedAt,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: command,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  return (
    <div className="cpo-sessions-panel">
      {/* ── Top bar ── */}
      <div className="cpo-sessions-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:ocpiCommands')}</h3>
          <p className="cpo-panel-subtitle">
            {commands.length > 0
              ? `${commands.length} ${t(
                  commands.length !== 1 ? 'ocpi:commandCountPlural' : 'ocpi:commandCountSingular',
                )} ${t('ocpi:forThisEmspRole')}`
              : t('ocpi:commandsEmptyDescription')}
          </p>
        </div>
      </div>

      {/* ── Commands Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateEMSPCommandsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchEmspCommandFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedCommand(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
      />

      {/* ── Drill-down modal ── */}
      {selectedCommand && (
        <CommandDrillDownModal command={selectedCommand} onClose={() => setSelectedCommand(null)} />
      )}
    </div>
  );
}

EmspCommandsPanel.defaultProps = {
  filterCountryCode: undefined,
  filterPartyId: undefined,
  refreshTrigger: undefined,
};
